'use client';

/**
 * ProgramCompareCloneManager — Phase 2 of the per-program field config roadmap.
 *
 * Two jobs that previously lived in spreadsheets:
 *   1. Compare matrix — every active program's effective hidden/required state
 *      per field, with divergent rows flagged, so you can see at a glance where
 *      programs differ.
 *   2. Clone — copy one program's per-program overrides onto another, so a new
 *      program can start from a known-good sibling instead of hand-rebuilding.
 *
 * Org→org cloning and blueprint bundles are handled separately by the existing
 * config_migration API (/api/config/export|import); this panel is the
 * program-level companion the multi-program onboarding pain actually needs.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { membersAPI, ProgramCompareResponse, ProgramCompareRow } from '@/services/api/members';

// Is this field configured differently across the programs it appears in?
function rowDiverges(row: ProgramCompareRow, programIds: number[]): boolean {
  const states = programIds.map((pid) => {
    const c = row.cells[String(pid)];
    // missing cell == not present for that program; treat as a distinct state
    return c ? `${c.is_hidden ? 'h' : 'v'}${c.is_required ? 'r' : ''}` : 'absent';
  });
  return new Set(states).size > 1;
}

function CellBadge({ cell }: { cell?: { is_hidden: boolean; is_required: boolean } }) {
  if (!cell) return <span className="text-xs text-muted-foreground">—</span>;
  if (cell.is_hidden) return <Badge variant="outline">Hidden</Badge>;
  if (cell.is_required) return <Badge>Required</Badge>;
  return <Badge variant="secondary">Visible</Badge>;
}

export default function ProgramCompareCloneManager() {
  const [data, setData] = useState<ProgramCompareResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [onlyDiff, setOnlyDiff] = useState(false);

  const [sourceId, setSourceId] = useState<number | null>(null);
  const [targetId, setTargetId] = useState<number | null>(null);
  const [mode, setMode] = useState<'replace' | 'merge'>('replace');
  const [cloning, setCloning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await membersAPI.compareProgramFields('member'));
    } catch (e: any) {
      setError(e?.message || 'Failed to load comparison');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const programIds = useMemo(() => (data?.programs ?? []).map((p) => p.id), [data]);
  const visibleRows = useMemo(() => {
    const rows = data?.rows ?? [];
    return onlyDiff ? rows.filter((r) => rowDiverges(r, programIds)) : rows;
  }, [data, onlyDiff, programIds]);

  const clone = async () => {
    if (sourceId == null || targetId == null) return;
    if (sourceId === targetId) {
      setError('Source and target must differ');
      return;
    }
    setCloning(true);
    setError(null);
    setMessage(null);
    try {
      const res = await membersAPI.cloneProgramOverrides(targetId, sourceId, mode);
      setMessage(`Cloned ${res.cloned} override(s).`);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Clone failed');
    } finally {
      setCloning(false);
    }
  };

  const programs = data?.programs ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Clone Program Overrides</CardTitle>
          <p className="text-sm text-muted-foreground">
            Copy one program&apos;s per-program field overrides onto another. Use
            &quot;replace&quot; to start the target fresh from the source, or
            &quot;merge&quot; to layer the source on top of the target&apos;s existing
            overrides.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-56">
              <div className="mb-1 text-xs text-muted-foreground">Copy from</div>
              <Select
                value={sourceId != null ? String(sourceId) : ''}
                onValueChange={(v) => setSourceId(v ? Number(v) : null)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Source program…" />
                </SelectTrigger>
                <SelectContent>
                  {programs.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-56">
              <div className="mb-1 text-xs text-muted-foreground">Copy to</div>
              <Select
                value={targetId != null ? String(targetId) : ''}
                onValueChange={(v) => setTargetId(v ? Number(v) : null)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Target program…" />
                </SelectTrigger>
                <SelectContent>
                  {programs.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <div className="mb-1 text-xs text-muted-foreground">Mode</div>
              <Select value={mode} onValueChange={(v) => setMode(v as 'replace' | 'merge')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="replace">Replace</SelectItem>
                  <SelectItem value="merge">Merge</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={clone}
              disabled={cloning || sourceId == null || targetId == null}
            >
              {cloning ? 'Cloning…' : 'Clone'}
            </Button>
          </div>
          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
          {message && <p className="mt-3 text-sm text-green-600">{message}</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Field Comparison Across Programs</CardTitle>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={onlyDiff}
                onChange={(e) => setOnlyDiff(e.target.checked)}
              />
              Only show fields that differ
            </label>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : !data || programs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No active programs to compare.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Field</TableHead>
                  {programs.map((p) => (
                    <TableHead key={p.id} className="text-center">
                      {p.name}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleRows.map((row) => {
                  const diverges = rowDiverges(row, programIds);
                  return (
                    <TableRow
                      key={`${row.is_core_field ? 'c' : 'x'}:${row.field_key}`}
                      className={diverges ? 'bg-amber-50' : undefined}
                    >
                      <TableCell>
                        <div className="font-medium">{row.display_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {row.category_name} · {row.field_key}
                        </div>
                      </TableCell>
                      {programs.map((p) => (
                        <TableCell key={p.id} className="text-center">
                          <CellBadge cell={row.cells[String(p.id)]} />
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
